<template>
  <Transition name="fade" mode="out-in">
    <div v-if="searching" class="py-2 max-md:px-3">
      <TextInput
        ref="searchRef"
        :modelValue="query"
        @update:modelValue="$emit('update:query', $event)"
        :placeholder="$t('components.inputs.type_to_search')"
      >
        <template #start>
          <Icon name="search" size="lg" />
        </template>
        <template #end>
          <Button
            v-show="query"
            :label="$t('components.buttons.cancel')"
            variant="link"
            type="gray"
            @click.stop="closeSearch"
          />
          <Icon
            v-show="!query"
            name="close"
            size="lg"
            :aria-label="$t('components.buttons.close')"
            class="hover:cursor-pointer"
            @click.stop="closeSearch"
          />
        </template>
      </TextInput>
    </div>
    <Tabs
      v-else
      :modelValue="modelValue"
      @update:modelValue="$emit('update:modelValue', $event)"
      :orientation="orientation"
      :variant="variant"
      :tabVariant="tabVariant"
      :size="size"
      :fullWidth="fullWidth"
      :defaultTab="defaultTab"
      :hideContent="true"
      :class="
        cn('h-14 justify-end max-md:ltr:pr-3 max-md:rtl:pl-3', props.class)
      "
    >
      <slot />
      <template #end>
        <IconButton
          variant="tertiary"
          type="gray"
          :aria-label="$t('components.buttons.search')"
          @click.stop="searching = true"
        >
          <Icon name="search" />
        </IconButton>
      </template>
    </Tabs>
  </Transition>
</template>

<script setup>
import { nextTick, ref, watch } from "vue";
import { Button, IconButton } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { TextInput } from "@/SharedComponents/Inputs";
import { Tabs } from "@/SharedComponents/Tabs";
import { cn } from "@/Utils/cn";

const emit = defineEmits(["update:modelValue", "update:query"]);

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  query: { type: String, default: "" },
  orientation: { type: String, required: false },
  variant: { type: String, required: false },
  tabVariant: { type: String, required: false },
  size: { type: String, required: false },
  fullWidth: { type: Boolean, default: false },
  defaultTab: { type: [String, Number], default: 0 },
  class: { type: null, required: false },
});

const searching = ref(false);

function closeSearch() {
  searching.value = false;
  emit("update:query", "");
}

const searchRef = ref(null);

watch(searchRef, (value) => {
  if (value) {
    nextTick(() => {
      searchRef.value?.focus();
    });
  }
});

defineExpose({
  searching,
  closeSearch,
});
</script>
